import React, { useState } from 'react';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';
import { defineMessages, useIntl } from 'react-intl';
import { Handshake, MapPin } from '@phosphor-icons/react';
import { BreadcrumbNavigation } from '../../../components/BreadcrumbNavigation';
import { Link } from '../../../components/Link';
import { CompanyLogo } from '../../../components/CompanyLogo';
import { mapEmployerScoreToTier } from '../../../utils/employer-score/mapScoreToTier';
import { isNetworkWorthy } from '../../../utils/best-companies/is-network-worthy';
import { companyProfileUrlPath } from '../../../utils/routing/company-profile-url-path';
import { salaryHubUrlPath } from '../../../utils/routing/salary-url-path';
import { JOBS_LISTING_SEGMENT } from '../../../utils/constants/url-constants';
import type { Data, ExamplePosting, HiringCompanies, HiringCompany } from './+data.server';
import type { SalaryStatistics } from '../../../lambda/types';
import type { SnapshotTrend } from '../../../lambda/utils/marketSnapshotQuery';
import { SALARY_ROLE_PAGE_SCRIPT_ID, getSalaryRoleDataFromScript } from './parseSalaryRoleInitialScript';
import type { MessageDescriptor } from 'react-intl';
import { finnishRoleSentenceLead, MIN_SAMPLE_FOR_TITLE_FIGURE, MIN_SAMPLE_TO_LEAD_OVER_OFFICIAL } from '../../../utils/salary/salary-role-seo-meta';
import { netMonthlyFor } from '../../../utils/salary/net-pay-grid';
import { Msg, useFormatMessage } from '../../../i18n-review/client/Msg';
import { RoleFollowButton } from '../../../components/roles/RoleFollowButton';
import { TaxIcebergLink } from '../../../components/tax-iceberg/TaxIcebergLink';
import { HubCrossLinks } from '../../../components/hub/HubCrossLinks';
import type { HubCrossLink } from '../../../types/hub-cross-link';
import { buildHubIndexLinks, hubLinksHeading } from '../../../utils/seo/hub-index-links';
import { findRoleByDisplayName, findRoleForAnyLocaleTitle } from '../../../utils/role/find-role-for-title';
import { buildRoleHubPath, resolveRoleHubSlug } from '../../../utils/role/role-hub-config';
import { finnishSalaryTitleSubject, joinNames } from '../../../utils/salary/salary-role-seo-meta';
import { getFinnishRoleGenitiveOrNull } from '../../../utils/i18n/finnish-role-genitive';
import { RoleViewTabs } from '../../../components/roles/RoleViewTabs';
import { salaryRoleUrlPath } from '../../../utils/routing/salary-url-path';
import { computeSectorGap, percentVs, shortEmployerName } from '../../../utils/salary/salary-role-insights';
import { Button } from '../../../design-system/ui/Button/Button';
import { Card } from '../../../design-system/ui/Card/Card';
import { Chip } from '../../../design-system/ui/Chip/Chip';
import { GradeBadge } from '../../../design-system/ui/GradeBadge/GradeBadge';
import { InlineAlert } from '../../../design-system/ui/InlineAlert/InlineAlert';
import { MarketBar } from '../../../design-system/ui/MarketBar/MarketBar';
import { PageContainer } from '../../../design-system/ui/PageContainer/PageContainer';
import { PageHeader } from '../../../design-system/ui/PageHeader/PageHeader';
import { Readout } from '../../../design-system/ui/Readout/Readout';
import { SegmentedControl } from '../../../design-system/ui/SegmentedControl/SegmentedControl';
import { TrendChip } from '../../../design-system/ui/TrendChip/TrendChip';
import { RangeChart } from '../../../design-system/ui/charts/RangeChart';
import { SalaryTemplate } from '../../../design-system/ui/templates/SalaryTemplate';
import { TemplateSection } from '../../../design-system/ui/templates/Section';
import { v2Messages } from './components/salary-role-v2.messages';
import {
  AdsAskOffer,
  EmployerPayChart,
  InsightTiles,
  OfficialEarningsPanel,
  Provenance,
  RoleLevelLadder,
  SalaryFaq,
  TakeHomeTable,
  type TakeHomeRow,
  TesMinimumSection,
  tesTextValues,
  OccupationAbout,
  RoleIntro,
  SectorFinding,
  TopPayingJobs,
  eur,
  levelMessage,
  type FaqItem,
} from './components/SalaryRoleSections';
import { PayCheck } from './components/PayCheck';

const messages = defineMessages({
  breadcrumbSalaryData: {
    id: 'salaryRolePage.breadcrumb.salaryData',
    defaultMessage: 'Salary Data',
    description: 'Breadcrumb link back to the salary hub from a role salary page',
  },
  heading: {
    id: 'salaryRolePage.heading',
    defaultMessage: '{roleTitle} Salary in Finland',
    description: 'Main heading of a role salary page, where {roleTitle} is the job title',
  },
  intro: {
    id: 'salaryRolePage.intro',
    defaultMessage: 'Based on job listing data from Finnish employers. Updated regularly.',
    description: 'Introductory sentence on a role salary page explaining the data source',
  },
  alsoKnownAs: {
    id: 'salaryRolePage.alsoKnownAs',
    defaultMessage: 'Also listed under: {titles}',
    description:
      'Shown when this role is also posted under other job titles that this page covers too, so a visitor who searched one of those titles can see they landed on the right page. {titles} is a comma-separated list of English job titles.',
  },
  seeOpenJobs: {
    id: 'salaryRolePage.seeOpenJobs',
    defaultMessage: 'See open {roleTitle} jobs →',
    description: 'Link to search results filtered by this role, where {roleTitle} is the job title',
  },
  overallPanelTitle: {
    id: 'salaryRolePage.overallPanel.title',
    defaultMessage: '{roleTitle}: Finland Average',
    description: 'Title of the national salary stats panel, where {roleTitle} is the job title',
  },
  statMedian: {
    id: 'salaryRolePage.stat.median',
    defaultMessage: 'Median',
    description: 'Label for the median salary stat',
  },
  statP25: {
    id: 'salaryRolePage.stat.p25',
    defaultMessage: 'P25',
    description: 'Label for the 25th percentile salary stat',
  },
  statP75: {
    id: 'salaryRolePage.stat.p75',
    defaultMessage: 'P75',
    description: 'Label for the 75th percentile salary stat',
  },
  statSampleSize: {
    id: 'salaryRolePage.stat.sampleSize',
    defaultMessage: 'Sample size',
    description: 'Label for the number of job listings a stat is based on',
  },
  statAverage: {
    id: 'salaryRolePage.stat.average',
    defaultMessage: 'Average',
    description: 'Label for the mean salary stat, shown in the range-bar detail popover',
  },
  statP90: {
    id: 'salaryRolePage.stat.p90',
    defaultMessage: 'P90',
    description: 'Label for the 90th percentile salary stat, shown in the range-bar detail popover',
  },
  statRange: {
    id: 'salaryRolePage.stat.range',
    defaultMessage: 'Full range',
    description: 'Label for the absolute min-max salary range, shown in the range-bar detail popover',
  },
  statMiddle50: {
    id: 'salaryRolePage.stat.middle50',
    defaultMessage: 'Middle 50%',
    description: 'Label for the P25–P75 band, shown in the range-bar detail popover',
  },
  rangeBarHint: {
    id: 'salaryRolePage.rangeBar.hint',
    defaultMessage: 'Tap or hover for details.',
    description: 'Hint that the salary range bar opens a popover with more stats',
  },
  rangeBarPopoverListings: {
    id: 'salaryRolePage.rangeBar.popoverListings',
    defaultMessage: '{count, plural, one {Based on # job listing} other {Based on # job listings}}',
    description: 'Footnote in the range-bar detail popover stating the sample size',
  },
  overallPanelFootnote: {
    id: 'salaryRolePage.overallPanel.footnote',
    defaultMessage: 'Monthly EUR · P25–P75 band with median marker · based on {count} job listings',
    description: 'Footnote under the national salary panel, where {count} is the sample size',
  },
  cityTableTitle: {
    id: 'salaryRolePage.cityTable.title',
    defaultMessage: '{roleTitle} Salary by City',
    description: 'Title of the per-city salary breakdown table, where {roleTitle} is the job title',
  },
  cityTableFootnote: {
    id: 'salaryRolePage.cityTable.footnote',
    defaultMessage: 'Bar shows P25–P75 band. Vertical line = median. All values in EUR/month.',
    description: 'Footnote under the per-city salary table',
  },
  ctaBanner: {
    id: 'salaryRolePage.ctaBanner',
    defaultMessage: 'Looking for {roleTitle} roles? {link}',
    description:
      'Call-to-action banner linking to open positions, where {roleTitle} is the job title and {link} is the link text',
  },
  ctaBannerLink: {
    id: 'salaryRolePage.ctaBanner.link',
    defaultMessage: 'Browse open positions on JobCrawls →',
    description: 'Link text inside the call-to-action banner',
  },
  disclaimer: {
    id: 'salaryRolePage.disclaimer',
    defaultMessage:
      "Salary data is derived from published job listings and may not represent total compensation. Figures are in EUR/month and may include JobCrawls estimates for listings that don't publish a salary.",
    description: 'Legal-style disclaimer at the bottom of a role salary page',
  },
  methodologyLink: {
    id: 'salaryRolePage.methodologyLink',
    defaultMessage: 'How we calculate these numbers',
    description: 'Link from a role salary page to the salary methodology page',
  },
  exposureHeading: {
    id: 'salaryRolePage.exposure.heading',
    defaultMessage: 'AI & automation exposure',
    description: 'Heading of the AI/automation-exposure section on a role salary page (TEC-485)',
  },
  exposureMatchBasis: {
    id: 'salaryRolePage.exposure.matchBasis',
    defaultMessage: 'Estimate for the broader occupation category: {occupationGroup}',
    description:
      'Transparency line under the exposure heading, showing which broad published occupation category (e.g. "ICT professionals") this role was matched to for the score. The number is not unique to this exact job title, {occupationGroup} is that category name',
  },
  exposureBandLow: {
    id: 'salaryRolePage.exposure.band.low',
    defaultMessage: 'Low exposure',
    description:
      'Exposure band label shown on a role salary page: this role is relatively insulated from AI/automation',
  },
  exposureBandMedium: {
    id: 'salaryRolePage.exposure.band.medium',
    defaultMessage: 'Medium exposure',
    description: 'Exposure band label shown on a role salary page: this role is partly exposed to AI/automation',
  },
  exposureBandHigh: {
    id: 'salaryRolePage.exposure.band.high',
    defaultMessage: 'High exposure',
    description: 'Exposure band label shown on a role salary page: this role is highly exposed to AI/automation',
  },
  exposureAugmentationNote: {
    id: 'salaryRolePage.exposure.augmentationNote',
    defaultMessage: 'Research suggests AI is more likely to change parts of this job than replace it entirely.',
    description:
      'Reassuring note shown alongside a medium/high exposure band, to avoid alarmist framing (TEC-485 non-goal)',
  },
  exposureExposedTasksHeading: {
    id: 'salaryRolePage.exposure.exposedTasksHeading',
    defaultMessage: 'Tasks more exposed to AI',
    description: 'Heading above the list of AI-exposed tasks for this role',
  },
  exposureResilientTasksHeading: {
    id: 'salaryRolePage.exposure.resilientTasksHeading',
    defaultMessage: 'Tasks less exposed to AI',
    description: 'Heading above the list of AI-resilient tasks for this role',
  },
  exposureRelatedRolesHeading: {
    id: 'salaryRolePage.exposure.relatedRolesHeading',
    defaultMessage: 'Lower-exposure roles to consider',
    description: 'Heading above a list of related roles with a lower automation-exposure band',
  },
  exposureSourceLabel: {
    id: 'salaryRolePage.exposure.sourceLabel',
    defaultMessage: 'Source: {source}',
    description: 'Citation line under the exposure section, {source} is the name of the published methodology cited',
  },
  remotePanelTitle: {
    id: 'salaryRolePage.remotePanel.title',
    defaultMessage: '{roleTitle}: Remote Work in Finland',
    description: 'Title of the nationwide remote-work salary panel, where {roleTitle} is the job title',
  },
  examplePostingsTitle: {
    id: 'salaryRolePage.examplePostings.title',
    defaultMessage: 'Based on postings like these',
    description: 'Heading for the list of real job postings a salary stat was computed from',
  },
  examplePostingInactive: {
    id: 'salaryRolePage.examplePostings.inactiveBadge',
    defaultMessage: 'No longer active',
    description: 'Small badge on an example posting card whose listing has expired',
  },
  trendUp: {
    id: 'salaryRolePage.trend.up',
    defaultMessage: '▲ {percent}% vs last month',
    description: 'Badge showing the median salary increased since last month, where {percent} is the change',
  },
  trendDown: {
    id: 'salaryRolePage.trend.down',
    defaultMessage: '▼ {percent}% vs last month',
    description: 'Badge showing the median salary decreased since last month, where {percent} is the absolute change',
  },
  trendFlat: {
    id: 'salaryRolePage.trend.flat',
    defaultMessage: 'No change vs last month',
    description: 'Badge shown when the median salary is unchanged from last month',
  },
  perMonth: {
    id: 'salary.period.month',
    defaultMessage: '/mo',
    description: 'Short abbreviation shown after a salary figure for a monthly pay period',
  },
  hiringCompaniesTitle: {
    id: 'salaryRolePage.hiringCompanies.title',
    defaultMessage: 'Companies hiring {roleTitle}s',
    description: 'Heading above the list of companies known to hire for this role, where {roleTitle} is the job title',
  },
  hiringCompaniesSub: {
    id: 'salaryRolePage.hiringCompanies.sub',
    defaultMessage:
      'From our employer rankings. Some may be worth reaching out to directly even without an open listing.',
    description: 'Subtitle under the companies-hiring-for-this-role section on a salary role page',
  },
  hiringCompaniesLiveSub: {
    id: 'salaryRolePage.hiringCompanies.liveSub',
    defaultMessage:
      '{count, plural, one {# employer has an open job for this role right now.} other {# employers have open jobs for this role right now.}}',
    description:
      'Subtitle under the companies-hiring section when it lists every employer with an open posting for the role; {count} is the number of employers',
  },
  hiringCompaniesOpenJobs: {
    id: 'salaryRolePage.hiringCompanies.openJobs',
    defaultMessage: '{count, plural, one {# open job} other {# open jobs}}',
    description: 'Under an employer name in the companies-hiring section: how many open postings it has for this role',
  },
  hiringCompaniesShowAll: {
    id: 'salaryRolePage.hiringCompanies.showAll',
    defaultMessage: 'Show all {count} employers',
    description:
      'Toggle that expands the full list of employers hiring for this role; {count} is the total number of employers',
  },
  hiringCompaniesEmpty: {
    id: 'salaryRolePage.hiringCompanies.empty',
    defaultMessage: 'Browse all ranked employers to find one hiring for this kind of role.',
    description: 'Shown when no specific company match was found for this role on the best-companies data',
  },
  hiringCompaniesBrowseAll: {
    id: 'salaryRolePage.hiringCompanies.browseAll',
    defaultMessage: 'Browse best employers in Finland →',
    description: 'Link to the best-companies hub from the salary role page',
  },
  hiringCompaniesGrade: {
    id: 'salaryRolePage.hiringCompanies.grade',
    defaultMessage: 'Grade {grade}',
    description:
      'Accessible name of an employer\'s Employer Score letter grade in the companies-hiring list, e.g. "Grade B"',
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Below this many postings a city row is faded and the remote panel hidden: indicative, not a market figure. */
const MIN_CITY_SAMPLE = 10;

/** Fewer ads than this and the ads median is not quoted as the going rate (same rule as the title). */
const MIN_ADS_FOR_FIGURE = MIN_SAMPLE_FOR_TITLE_FIGURE;

/** Monthly to hourly: full-time monthly pay over 160 working hours, the usual Finnish rule of thumb. */
const HOURS_PER_MONTH = 160;

/** One FAQ question and answer, kept as messages so the page and the JSON-LD say the same thing. */
interface FaqEntry {
  q: MessageDescriptor;
  a: MessageDescriptor;
  aValues: Record<string, string | number>;
}

/** "17,80 €" / "€17.80": hourly pay to the nearest ten cents, never a whole-euro rounding. */
function eurCents(value: number, locale: 'en' | 'fi' | 'sv'): string {
  const rounded = Math.round(value * 10) / 10;
  const n = new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rounded);
  return locale === 'en' ? `€${n}` : `${n}\u00a0€`;
}

/** JSON-LD text: the page's non-breaking spaces as plain ones. */
function plainText(value: string): string {
  return value.replace(/[\u00a0\u202f]/g, ' ');
}

function medianMid(stats: SalaryStatistics): number | null {
  if (!stats.median) return null;
  return (stats.median.min + stats.median.max) / 2;
}

/** The year the page's figures refer to (stats row's lastUpdated), so title, H1 and SSR/hydration agree. */
function figuresYear(data: Data): number {
  const raw = data.overallStats?.lastUpdated;
  const d = raw ? new Date(raw) : null;
  return d && !Number.isNaN(d.getTime()) ? d.getFullYear() : new Date().getFullYear();
}

/** The two pay ranges the hero bar and the remote panel draw: middle half and median of one stats row. */
function rangeOf(stats: SalaryStatistics) {
  const min = stats.min ?? 0;
  const max = stats.max ?? 0;
  const median = medianMid(stats);
  return {
    median,
    p25: stats.percentile25?.min ?? null,
    p75: stats.percentile75?.max ?? null,
    p90: stats.percentile90?.max ?? null,
    min,
    max,
    sample: stats.sampleSize ?? stats.count ?? 0,
  };
}

function SalaryTrendChip({ trend }: { trend: SnapshotTrend | null | undefined }) {
  const formatMessage = useFormatMessage();
  if (!trend || trend.percentChange == null) return null;
  const { percentChange } = trend;
  const direction = percentChange === 0 ? 'flat' : percentChange > 0 ? 'up' : 'down';
  // The messages carry their own triangle glyph; the chip draws an arrow, so the glyph is dropped.
  const label = (
    percentChange === 0
      ? formatMessage(messages.trendFlat)
      : formatMessage(percentChange > 0 ? messages.trendUp : messages.trendDown, { percent: Math.abs(percentChange) })
  ).replace(/^[▲▼]\s*/, '');
  return (
    <TrendChip direction={direction} tone={direction === 'up' ? 'positive' : 'neutral'}>
      {label}
    </TrendChip>
  );
}

// ── Header, range and readout (the template's head) ───────────────────────────

/**
 * The answer first: the H1 with the year (matches the title), a lede that carries the search terms
 * and the take-home figure, the Jobs | Salary tabs that join the role's two pages.
 */
function SalaryHeader({
  data,
  roleTitle,
  headingTitle,
  otherTitles,
  locale,
  jobsHref,
  salaryHref,
  breadcrumbs,
}: {
  data: Data;
  roleTitle: string;
  headingTitle: string;
  otherTitles: string[];
  locale: 'en' | 'fi' | 'sv';
  jobsHref: string | null;
  salaryHref: string;
  breadcrumbs: React.ReactNode;
}) {
  const stats = data.overallStats;
  const { median, sample } = stats ? rangeOf(stats) : { median: null, sample: 0 };
  const average = stats?.average ? (stats.average.min + stats.average.max) / 2 : null;
  const net = data.netMedian ?? null;
  const year = figuresYear(data);
  const sectorGap = computeSectorGap(data.market?.summary);
  const sectorNames = (sector: 'public' | 'private') => {
    const names = (data.market?.summary.employers ?? [])
      .filter((e) => e.sector === sector)
      .map((e) => shortEmployerName(e.name));
    // Two names read well ("Attendo ja Esperi Care"); two long official names don't.
    return names.slice(0, 2).join('').length <= 32 ? joinNames(names.slice(0, 2), locale) : (names[0] ?? '');
  };
  const publicNames = sectorGap ? sectorNames('public') : '';
  const privateNames = sectorGap ? sectorNames('private') : '';
  // Too few ads to quote as the going rate: the official median leads when the role has one,
  // otherwise the ads sentence says how many ads it rests on.
  const thinAds = sample < MIN_ADS_FOR_FIGURE;
  const officialTotal = data.official?.total ?? null;
  const officialLeads = officialTotal != null && sample < MIN_SAMPLE_TO_LEAD_OVER_OFFICIAL;
  const officialNet = officialTotal ? netMonthlyFor(officialTotal.regularMedian) : null;
  const lede =
    officialLeads && officialTotal ? (
      <Msg
        {...v2Messages.ledeOfficial}
        values={{
          roleTitle,
          roleLead: finnishRoleSentenceLead(roleTitle),
          official: eur(officialTotal.regularMedian, locale),
          year: data.officialSource?.year ?? 2024,
          net: officialNet != null ? eur(officialNet, locale) : '',
          hasNet: officialNet != null ? 'yes' : 'no',
          count: sample,
        }}
      />
    ) : median != null && sample > 0 ? (
      <>
        <Msg
          {...v2Messages.ledeMain}
          values={{
            roleTitle,
            roleLead: finnishRoleSentenceLead(roleTitle),
            median: eur(median, locale),
            average: average != null ? eur(average, locale) : '',
            hasAverage: average != null ? 'yes' : 'no',
            net: net != null ? eur(net, locale) : '',
            hasNet: net != null ? 'yes' : 'no',
          }}
        />
        {thinAds ? (
          <>
            {' '}
            <Msg {...v2Messages.ledeThinNote} values={{ count: sample }} />
          </>
        ) : null}
        {sectorGap && publicNames && privateNames ? (
          <>
            {' '}
            <Msg
              {...v2Messages.ledeSectors}
              values={{
                publicNames,
                privateNames,
                publicPay: eur(sectorGap.publicPay, locale),
                privatePay: eur(sectorGap.privatePay, locale),
              }}
            />
          </>
        ) : null}
      </>
    ) : undefined;
  const tabFigure = officialLeads && officialTotal ? officialTotal.regularMedian : median;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={<Msg {...v2Messages.headingWithYear} values={{ roleTitle: headingTitle, year }} />}
        subtitle={lede}
        actions={<RoleFollowButton roleTitle={roleTitle} surface="salary-role-page" />}
      />
      {otherTitles.length > 0 ? (
        <p className="-mt-2 type-caption text-jc-ink-secondary">
          <Msg {...messages.alsoKnownAs} values={{ titles: otherTitles.join(', ') }} />
        </p>
      ) : null}
      <RoleViewTabs
        locale={locale}
        active="salary"
        jobsHref={jobsHref}
        jobsCount={data.market?.summary.openCount ?? null}
        salaryHref={salaryHref}
        salaryFigure={tabFigure != null ? eur(tabFigure, locale) : null}
      />
    </div>
  );
}

/** Where the pay sits: the middle half and the median on one bar, the sentence that says it, and the source line. */
function SalaryRange({ data, stats, locale }: { data: Data; stats: SalaryStatistics; locale: 'en' | 'fi' | 'sv' }) {
  const formatMessage = useFormatMessage();
  const { median, p25, p75, p90, sample } = rangeOf(stats);
  if (median == null) return null;
  const hasBand = p25 != null && p75 != null;
  const description = hasBand
    ? formatMessage(v2Messages.marketBarDesc, {
        median: eur(median, locale),
        p25: eur(p25, locale),
        p75: eur(p75, locale),
        count: sample,
      })
    : formatMessage(v2Messages.marketBarDescMedianOnly, { median: eur(median, locale), count: sample });
  return (
    <Card className="flex flex-col gap-4">
      <p className="type-eyebrow text-jc-ink-secondary">
        <Msg {...v2Messages.distTitle} />
      </p>
      <MarketBar
        variant="expanded"
        barSize="lg"
        median={median}
        p25={p25 ?? undefined}
        p75={p75 ?? undefined}
        sampleSize={sample}
        thinLabels={{ rough: formatMessage(v2Messages.thinRough), tooFew: formatMessage(v2Messages.thinTooFew) }}
        p25Label={p25 != null ? eur(p25, locale) : ''}
        p75Label={p75 != null ? eur(p75, locale) : ''}
        sample={<Msg {...v2Messages.provPostings} values={{ count: sample }} />}
        description={description}
      />
      {hasBand ? (
        <p className="text-jc-ink-secondary">
          <Msg
            {...v2Messages.distSentence}
            values={{
              p25: eur(p25, locale),
              p75: eur(p75, locale),
              p90: p90 != null ? eur(p90, locale) : '',
              hasP90: p90 != null ? 'yes' : 'no',
            }}
          />
        </p>
      ) : null}
      <Provenance data={data} locale={locale} />
    </Card>
  );
}

/** The headline figure on the forest readout: the national median, gross or take-home, with the trend and the tax-calculator link. */
function SalaryReadout({
  data,
  stats,
  trend,
  locale,
  showNet,
  onShowNet,
}: {
  data: Data;
  stats: SalaryStatistics;
  trend: SnapshotTrend | null | undefined;
  locale: 'en' | 'fi' | 'sv';
  showNet: boolean;
  onShowNet: (v: boolean) => void;
}) {
  const formatMessage = useFormatMessage();
  const { median, sample } = rangeOf(stats);
  if (median == null) return null;
  const net = data.netMedian ?? null;
  const figure = showNet && net != null ? net : median;
  return (
    <div className="flex flex-col gap-4">
      {net != null ? (
        <SegmentedControl
          className="self-start"
          label={formatMessage(v2Messages.toggleLabel)}
          value={showNet ? 'net' : 'gross'}
          onValueChange={(v) => onShowNet(v === 'net')}
          options={[
            { value: 'gross', label: formatMessage(v2Messages.toggleGross) },
            { value: 'net', label: formatMessage(v2Messages.toggleNet) },
          ]}
        />
      ) : null}
      <Readout
        label={formatMessage(messages.statMedian)}
        figure={eur(figure, locale)}
        unit={formatMessage(showNet ? v2Messages.heroUnitNet : v2Messages.heroUnitGross)}
        source={<Msg {...v2Messages.provPostings} values={{ count: sample }} />}
      />
      <div className="flex flex-wrap items-center gap-2">
        <SalaryTrendChip trend={trend} />
        {net != null && !showNet ? (
          <Chip>
            <Msg {...v2Messages.netChip} values={{ net: eur(net, locale) }} />
          </Chip>
        ) : null}
      </div>
      <p className="type-caption text-jc-ink-secondary">
        <Msg {...v2Messages.netNote} /> <TaxIcebergLink grossMonthly={median} size="sm" />
      </p>
    </div>
  );
}

// ── Pay by city ───────────────────────────────────────────────────────────────

function CityTable({
  cityStats,
  roleTitle,
  locale,
}: {
  cityStats: Array<{ city: string; stats: SalaryStatistics }>;
  roleTitle: string;
  locale: 'en' | 'fi' | 'sv';
}) {
  const formatMessage = useFormatMessage();
  const perMonth = formatMessage(messages.perMonth);
  const rows = cityStats
    .map(({ city, stats }) => {
      const r = rangeOf(stats);
      const cityName = city.split(',')[0];
      const median = r.median ?? ((r.p25 ?? r.min) + (r.p75 ?? r.max)) / 2;
      return {
        id: city,
        cityName,
        median,
        p25: r.p25 ?? r.min,
        p75: r.p75 ?? r.max,
        sample: r.sample,
      };
    })
    .sort((a, b) => b.median - a.median);
  // A breakdown whose every row is too thin to draw says nothing: leave it out.
  if (!rows.some((r) => r.sample >= MIN_CITY_SAMPLE)) return null;
  // The story row is the best-paid city whose middle half is drawn (20 or more ads), else the best-paid drawn one.
  const top = rows.find((r) => r.sample >= 20) ?? rows.find((r) => r.sample >= MIN_CITY_SAMPLE) ?? rows[0];
  return (
    <TemplateSection id="cities" title={<Msg {...messages.cityTableTitle} values={{ roleTitle }} />}>
      <RangeChart
        rows={rows.map((r) => ({
          id: r.id,
          label: r.cityName,
          p25: r.p25,
          p75: r.p75,
          median: r.median,
          sample: r.sample,
          display: `${eur(r.median, locale)}${perMonth}`,
        }))}
        highlightId={top.id}
        formatTick={(v) => eur(v, locale)}
        thinLabels={{ rough: formatMessage(v2Messages.thinRough), tooFew: formatMessage(v2Messages.thinTooFew) }}
        description={formatMessage(v2Messages.cityChartDesc, {
          count: rows.length,
          city: top.cityName,
          pay: eur(top.median, locale),
        })}
      />
      <p className="type-caption text-jc-ink-secondary">
        <Msg {...messages.cityTableFootnote} />
      </p>
    </TemplateSection>
  );
}

// ── Remote work ───────────────────────────────────────────────────────────────

function RemoteStatsPanel({
  stats,
  roleTitle,
  locale,
}: {
  stats: SalaryStatistics;
  roleTitle: string;
  locale: 'en' | 'fi' | 'sv';
}) {
  const formatMessage = useFormatMessage();
  const { median, p25, p75, sample } = rangeOf(stats);
  if (median == null) return null;
  const hasBand = p25 != null && p75 != null;
  return (
    <TemplateSection id="remote" title={<Msg {...messages.remotePanelTitle} values={{ roleTitle }} />}>
      <p className="type-figure text-jc-ink">
        {eur(median, locale)}
        <span className="text-sm font-normal text-jc-ink-secondary">{formatMessage(messages.perMonth)}</span>
      </p>
      <MarketBar
        variant="expanded"
        barSize="lg"
        median={median}
        p25={p25 ?? undefined}
        p75={p75 ?? undefined}
        sampleSize={sample}
        thinLabels={{ rough: formatMessage(v2Messages.thinRough), tooFew: formatMessage(v2Messages.thinTooFew) }}
        p25Label={p25 != null ? eur(p25, locale) : ''}
        p75Label={p75 != null ? eur(p75, locale) : ''}
        sample={<Msg {...v2Messages.provPostings} values={{ count: sample }} />}
        description={
          hasBand
            ? formatMessage(v2Messages.marketBarDesc, {
                median: eur(median, locale),
                p25: eur(p25, locale),
                p75: eur(p75, locale),
                count: sample,
              })
            : formatMessage(v2Messages.marketBarDescMedianOnly, { median: eur(median, locale), count: sample })
        }
      />
    </TemplateSection>
  );
}

// ── Example postings ─────────────────────────────────────────────────────────

function ExamplePostings({ postings }: { postings: ExamplePosting[] }) {
  if (postings.length === 0) return null;
  return (
    <TemplateSection id="examples" title={<Msg {...messages.examplePostingsTitle} />}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {postings.map((posting) => (
          <Card key={posting.href} as="article" interactive padding="sm" className="flex items-center gap-3">
            <CompanyLogo
              companyName={posting.companyId || posting.company}
              size={40}
              alt={posting.company}
              variant="rounded"
              isTyomarkkinatori={posting.isTyomarkkinatori}
              className={posting.isActive ? undefined : 'opacity-60'}
            />
            <div className="min-w-0 flex-1">
              <Link
                href={posting.href}
                className="block truncate text-sm font-semibold text-jc-ink no-underline after:absolute after:inset-0"
              >
                {posting.title}
              </Link>
              <p className="flex items-center gap-1 type-caption text-jc-ink-secondary">
                {posting.company ? <span className="truncate">{posting.company}</span> : null}
                {posting.company && posting.location ? <span aria-hidden>·</span> : null}
                {posting.location ? (
                  <span className="inline-flex min-w-0 items-center gap-0.5">
                    <MapPin className="size-3.5 shrink-0" weight="fill" aria-hidden />
                    <span className="truncate">{posting.location}</span>
                  </span>
                ) : null}
              </p>
            </div>
            {!posting.isActive ? (
              <Chip>
                <Msg {...messages.examplePostingInactive} />
              </Chip>
            ) : null}
          </Card>
        ))}
      </div>
    </TemplateSection>
  );
}

// ── Companies hiring for this role ──────────────────────────────────────────────

/** Employers shown as full cards before the "Show all" toggle. */
const HIRING_COMPANY_CARDS = 6;

function hiringCompanyHref(company: HiringCompany, locale: string): string {
  return company.employerScore !== undefined
    ? companyProfileUrlPath(locale as 'en' | 'fi' | 'sv', company.objectID, company.profileSlug)
    : (company.jobsHref ?? companyProfileUrlPath(locale as 'en' | 'fi' | 'sv', company.objectID, company.profileSlug));
}

/**
 * Bridges salary data back to company discovery. Matched server-side in +data.server.ts so every
 * link is in the initial HTML, including the ones inside the "Show all" toggle (a native
 * <details>, so crawlers and no-JS visitors get the full list). Live mode lists every employer
 * with an open posting for the role; scored employers link to their profile, the rest to their
 * open jobs in search.
 */
function CompaniesHiringForRole({
  roleTitle,
  locale,
  hiring,
}: {
  roleTitle: string;
  locale: string;
  hiring: HiringCompanies;
}) {
  const formatMessage = useFormatMessage();
  const { live, companies } = hiring;
  const cards = companies.slice(0, HIRING_COMPANY_CARDS);
  const more = companies.slice(HIRING_COMPANY_CARDS);
  return (
    <TemplateSection
      id="hiring"
      title={<Msg {...messages.hiringCompaniesTitle} values={{ roleTitle }} />}
      description={
        live ? (
          <Msg {...messages.hiringCompaniesLiveSub} values={{ count: companies.length }} />
        ) : (
          <Msg {...messages.hiringCompaniesSub} />
        )
      }
    >
      {companies.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {cards.map((company) => {
              const networkWorthy = isNetworkWorthy(company);
              const tier = company.employerScore !== undefined ? mapEmployerScoreToTier(company.employerScore) : null;
              return (
                <Card key={company.objectID} as="article" interactive padding="sm" className="flex items-center gap-3">
                  <CompanyLogo
                    companyName={company.objectID}
                    size={40}
                    alt={company.companyName}
                    variant="rounded"
                    isTyomarkkinatori={false}
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <Link
                      href={hiringCompanyHref(company, locale)}
                      locale={locale}
                      className="truncate text-sm font-semibold text-jc-ink no-underline after:absolute after:inset-0"
                    >
                      {company.companyName}
                    </Link>
                    {company.openJobs !== undefined ? (
                      <span className="truncate type-caption text-jc-ink-secondary">
                        <Msg {...messages.hiringCompaniesOpenJobs} values={{ count: company.openJobs }} />
                        {company.registryCity ? ` · ${company.registryCity}` : null}
                      </span>
                    ) : networkWorthy ? (
                      <span>
                        <Chip icon={Handshake}>
                          <Msg id="bestCompanies.row.networkWorthy" defaultMessage="Worth networking with" />
                        </Chip>
                      </span>
                    ) : company.registryCity ? (
                      <span className="truncate type-caption text-jc-ink-secondary">{company.registryCity}</span>
                    ) : null}
                  </div>
                  {tier ? (
                    <GradeBadge
                      grade={tier}
                      size="md"
                      accessibleLabel={formatMessage(messages.hiringCompaniesGrade, { grade: tier })}
                    />
                  ) : null}
                </Card>
              );
            })}
          </div>
          {more.length > 0 ? (
            <details className="group/more">
              <summary className="cursor-pointer list-none text-sm font-semibold text-jc-ink underline underline-offset-4 [&::-webkit-details-marker]:hidden">
                <Msg {...messages.hiringCompaniesShowAll} values={{ count: companies.length }} />
              </summary>
              <ul className="m-0 mt-3 grid list-none grid-cols-1 gap-x-6 gap-y-2 p-0 sm:grid-cols-2">
                {more.map((company) => (
                  <li key={company.objectID} className="flex min-w-0 items-baseline justify-between gap-2 text-sm">
                    <Link
                      href={hiringCompanyHref(company, locale)}
                      locale={locale}
                      className="truncate text-jc-ink hover:underline"
                    >
                      {company.companyName}
                    </Link>
                    {company.openJobs !== undefined ? (
                      <span className="flex-none type-caption text-jc-ink-secondary">
                        <Msg {...messages.hiringCompaniesOpenJobs} values={{ count: company.openJobs }} />
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-jc-ink-secondary">
          <Msg {...messages.hiringCompaniesEmpty} />{' '}
          <Link
            href="/best-companies"
            locale={locale}
            className="font-semibold text-jc-ink underline underline-offset-4"
          >
            <Msg {...messages.hiringCompaniesBrowseAll} />
          </Link>
        </p>
      )}
    </TemplateSection>
  );
}

const EXPOSURE_BAND_MESSAGE = {
  low: messages.exposureBandLow,
  medium: messages.exposureBandMedium,
  high: messages.exposureBandHigh,
} as const;

/**
 * TEC-485. `data.exposure` is only ever set when a human confirmed BOTH this role's ISCO
 * mapping and that code's OECD score (getPublishableExposureForRole, server/occupation-
 * exposure/get-role-exposure.ts) — never a guess, so this component has no "unscored"
 * state to render, it's simply absent from the page until reviewed data exists. Per the
 * ticket's brand-promise/non-alarmist requirement, medium/high bands always pair with the
 * augmentation note and, when available, lower-exposure alternative roles — never a bare
 * risk number. No band has a colour of its own (the palette has no warning hue): the band is
 * a word in a chip.
 */
function ExposureBadge({
  exposure,
  relatedRoles,
  locale,
}: {
  exposure: Data['exposure'];
  relatedRoles: Data['exposureRelatedRoles'];
  locale: string;
}) {
  if (!exposure) return null;
  const exposedTasks = exposure.exposedTasks?.[locale as 'en' | 'fi' | 'sv'] ?? exposure.exposedTasks?.en ?? [];
  const resilientTasks = exposure.resilientTasks?.[locale as 'en' | 'fi' | 'sv'] ?? exposure.resilientTasks?.en ?? [];
  return (
    <TemplateSection
      id="exposure"
      title={<Msg {...messages.exposureHeading} />}
      description={
        exposure.iscoTitle ? (
          <Msg {...messages.exposureMatchBasis} values={{ occupationGroup: exposure.iscoTitle }} />
        ) : undefined
      }
      action={
        <Chip>
          <Msg {...EXPOSURE_BAND_MESSAGE[exposure.exposureBand]} />
        </Chip>
      }
    >
      {exposure.exposureBand !== 'low' ? (
        <p className="text-sm text-jc-ink-secondary">
          <Msg {...messages.exposureAugmentationNote} />
        </p>
      ) : null}
      {exposedTasks.length > 0 || resilientTasks.length > 0 ? (
        <div className="grid gap-4 text-sm sm:grid-cols-2">
          {exposedTasks.length > 0 ? (
            <div className="flex flex-col gap-1">
              <h3 className="type-eyebrow text-jc-ink-secondary">
                <Msg {...messages.exposureExposedTasksHeading} />
              </h3>
              <ul className="m-0 list-inside list-disc space-y-1 p-0 text-jc-ink">
                {exposedTasks.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {resilientTasks.length > 0 ? (
            <div className="flex flex-col gap-1">
              <h3 className="type-eyebrow text-jc-ink-secondary">
                <Msg {...messages.exposureResilientTasksHeading} />
              </h3>
              <ul className="m-0 list-inside list-disc space-y-1 p-0 text-jc-ink">
                {resilientTasks.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
      {relatedRoles.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="type-eyebrow text-jc-ink-secondary">
            <Msg {...messages.exposureRelatedRolesHeading} />
          </h3>
          <div className="flex flex-wrap gap-2">
            {relatedRoles.map((r) => (
              <Button key={r.slug} href={r.href} variant="secondary" size="sm">
                {r.displayTitle}
              </Button>
            ))}
          </div>
        </div>
      ) : null}
      {exposure.sourceCitations.length > 0 ? (
        <p className="type-caption text-jc-ink-secondary">
          {exposure.sourceCitations.map((c, i) => (
            <React.Fragment key={c.url}>
              {i > 0 && ', '}
              <Msg {...messages.exposureSourceLabel} values={{ source: c.name }} />{' '}
              <a
                href={c.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-jc-ink underline underline-offset-2"
              >
                ↗
              </a>
            </React.Fragment>
          ))}
        </p>
      ) : null}
    </TemplateSection>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SalaryRolePage() {
  // useData()/usePageContext() reliably return undefined on the client in this app (see
  // parseSalaryRoleInitialScript.ts) — fall back to the SSR-embedded script payload, same
  // pattern as every other content page here (job detail, SRP, best-companies).
  const dataFromHook = useData<Data>();
  const data = dataFromHook ?? getSalaryRoleDataFromScript();
  const pageContext = usePageContext();
  const formatMessage = useFormatMessage();
  const [showNet, setShowNet] = useState(false);
  if (!data) {
    return null;
  }
  const {
    roleTitle,
    roleTitleDisplay,
    overallStats,
    cityStats,
    remoteStats,
    examplePostings,
    hiringCompanies,
    snapshotTrend,
    otherTitles,
    exposure,
    exposureRelatedRoles,
  } = data;
  const locale = (data.locale ?? pageContext?.locale ?? 'en') as string;
  const loc = (locale === 'fi' ? 'fi' : locale === 'sv' ? 'sv' : 'en') as 'en' | 'fi' | 'sv';
  const localePrefix = locale === 'en' ? '' : `/${locale}`;
  // srpUrl filters search results by normalisedTitle, which is indexed in English regardless of
  // page locale — must stay on the English roleTitle, never the localized display name. Must
  // include the locale's jobs-listing segment (JOBS_LISTING_SEGMENT) — the bare locale root
  // (e.g. "/en/?normalisedTitle=...") renders the marketing landing page, not the SRP, so the
  // filter query param was silently ignored and every "See open X jobs" link landed on the
  // unfiltered homepage instead of the filtered search results.
  const srpUrl = `${localePrefix}/${JOBS_LISTING_SEGMENT[locale as 'en' | 'fi' | 'sv']}?normalisedTitle=${encodeURIComponent(roleTitle)}`;
  const breadcrumbHref = salaryHubUrlPath(loc);
  // Everything below is visible page text, not a data key — uses roleTitleDisplay (the fi/sv
  // name when a real posting supplied one, English otherwise) so the page actually contains the
  // words a Finnish/Swedish searcher typed, instead of an English role name wrapped in fi/sv
  // sentences.
  const displayTitle = roleTitleDisplay ?? roleTitle;
  // The fi H1 is the form people search: bare ("Sairaanhoitaja palkka", 6 900/mo vs 800/mo for
  // the genitive) except for the few roles where the genitive wins (finnishSalaryTitleSubject).
  // The genitive always appears in the lede sentence below it, so the page carries both forms.
  const headingTitle = loc === 'fi' ? finnishSalaryTitleSubject(displayTitle) : displayTitle;
  const hubLocale = loc;
  // The role's jobs page: the role hub when one exists (indexable, the "Työpaikat" tab), else the
  // filtered search, which still answers "show me these jobs".
  const jobsHref = data.roleHub?.href ?? srpUrl;
  const salaryHref = salaryRoleUrlPath(loc, data.roleSlugsByLocale?.[loc] ?? data.roleSlug);
  const median = overallStats ? medianMid(overallStats) : null;
  const sampleSize = overallStats?.sampleSize ?? overallStats?.count ?? 0;
  const year = figuresYear(data);

  // FAQ: the questions Google shows for these searches ("Mikä on sairaanhoitajan peruspalkka?",
  // "Mikä sairaanhoitaja saa eniten palkkaa?", "Onko sairaanhoitajan palkka huono?", "Paljonko
  // palkka nousee?", "Paljonko on ... tuntipalkka?", "Paljonko ... jää käteen?"), each answered only
  // from numbers on this page, and left out when we have none. With fewer than
  // MIN_ADS_FOR_FIGURE ads the ads median is not the going rate: Statistics Finland answers when
  // the role is mapped (below MIN_SAMPLE_TO_LEAD_OVER_OFFICIAL), and the "is it good / is it
  // rising" answers that rest on the ads drop out.
  const roleLead =
    loc === 'fi' ? (getFinnishRoleGenitiveOrNull(displayTitle)?.toLowerCase() ?? displayTitle) : displayTitle;
  const roleNom = loc === 'en' ? displayTitle : displayTitle.toLowerCase();
  const thinAds = sampleSize < MIN_ADS_FOR_FIGURE;
  const officialTotal = data.official?.total ?? null;
  // Statistics Finland answers while the ads are under the page's own "too few" bar.
  const officialLeads = officialTotal != null && sampleSize < MIN_SAMPLE_TO_LEAD_OVER_OFFICIAL;
  const officialYear = data.officialSource?.year ?? 2024;
  // The figure the page leads with: the ads median, or the official one when the ads are too few.
  const leadPay =
    officialLeads && officialTotal
      ? { source: 'official' as const, monthly: officialTotal.regularMedian }
      : median != null
        ? { source: 'ads' as const, monthly: median }
        : null;
  const qValues = { roleTitle: roleNom, roleLead, roleNom };
  const faqEntries: FaqEntry[] = [];
  if (officialLeads && officialTotal) {
    const net = netMonthlyFor(officialTotal.regularMedian);
    faqEntries.push({
      q: v2Messages.faqQPay,
      a: v2Messages.faqAPayOfficial,
      aValues: {
        official: eur(officialTotal.regularMedian, loc),
        year: officialYear,
        hasBand: officialTotal.regularP10 && officialTotal.regularP90 ? 'yes' : 'no',
        p10: officialTotal.regularP10 ? eur(officialTotal.regularP10, loc) : '',
        p90: officialTotal.regularP90 ? eur(officialTotal.regularP90, loc) : '',
        net: net != null ? eur(net, loc) : '',
        hasNet: net != null ? 'yes' : 'no',
      },
    });
  } else if (thinAds && median != null && sampleSize > 0) {
    faqEntries.push({
      q: v2Messages.faqQPay,
      a: v2Messages.faqAPayThin,
      aValues: { count: sampleSize, median: eur(median, loc) },
    });
  } else if (median != null && overallStats?.percentile25 && overallStats?.percentile75) {
    faqEntries.push({
      q: v2Messages.faqQPay,
      a: v2Messages.faqAPay,
      aValues: {
        median: eur(median, loc),
        p25: eur(overallStats.percentile25.min, loc),
        p75: eur(overallStats.percentile75.max, loc),
        net: data.netMedian != null ? eur(data.netMedian, loc) : '',
        hasNet: data.netMedian != null ? 'yes' : 'no',
      },
    });
  }
  if (leadPay) {
    const net = leadPay.source === 'official' ? netMonthlyFor(leadPay.monthly) : data.netMedian;
    if (net != null) {
      faqEntries.push({
        q: v2Messages.faqQNet,
        a: v2Messages.faqANet,
        aValues: { gross: eur(leadPay.monthly, loc), net: eur(net, loc) },
      });
    }
    faqEntries.push({
      q: v2Messages.faqQHourly,
      a: v2Messages.faqAHourly,
      aValues: {
        hourly: eurCents(leadPay.monthly / HOURS_PER_MONTH, loc),
        monthly: eur(leadPay.monthly, loc),
        source: leadPay.source,
      },
    });
  }
  if (data.tes) {
    faqEntries.push({ q: v2Messages.faqQTes, a: v2Messages.tesText, aValues: tesTextValues(data.tes, loc) });
  }
  // "Aloituspalkka": only from figures that are about starting out, the junior-level ads and the
  // agreement's entry step. Statistics Finland's lowest tenth is not starting pay, so it stays out.
  {
    const level = (name: string) => (data.roleLevels ?? []).find((l) => l.level === name && l.n >= 5) ?? null;
    const junior = level('Junior');
    const senior = level('Senior');
    const tesStart = data.tes
      ? data.tes.monthly != null
        ? formatMessage(v2Messages.tesPerMonth, { amount: eur(data.tes.monthly, loc) })
        : data.tes.hourly != null
          ? formatMessage(v2Messages.tesPerHour, { amount: eurCents(data.tes.hourly, loc) })
          : null
      : null;
    if (junior || tesStart) {
      faqEntries.push({
        q: v2Messages.faqQStart,
        a: v2Messages.faqAStart,
        aValues: {
          hasJunior: junior ? 'yes' : 'no',
          junior: junior ? eur(junior.median, loc) : '',
          hasSenior: junior && senior ? 'yes' : 'no',
          senior: senior ? eur(senior.median, loc) : '',
          hasTes: tesStart ? 'yes' : 'no',
          tes: tesStart ?? '',
        },
      });
    }
  }
  {
    const topEmployer = [...(data.market?.summary.employers ?? [])].sort((a, b) => b.median - a.median)[0] ?? null;
    const topCity =
      [...cityStats]
        .filter((cs) => (cs.stats.sampleSize ?? cs.stats.count ?? 0) >= MIN_CITY_SAMPLE)
        .sort((a, b) => (medianMid(b.stats) ?? 0) - (medianMid(a.stats) ?? 0))[0] ?? null;
    const topLevel =
      [...(data.roleLevels ?? [])].filter((l) => l.n >= 10).sort((a, b) => b.median - a.median)[0] ?? null;
    const levelMsg = topLevel ? levelMessage(topLevel.level) : null;
    if (topEmployer || topCity || (topLevel && levelMsg && (data.roleLevels?.length ?? 0) >= 2)) {
      faqEntries.push({
        q: v2Messages.faqQWhoMost,
        a: v2Messages.faqAWhoMost,
        aValues: {
          hasEmployer: topEmployer ? 'yes' : 'no',
          employer: topEmployer ? shortEmployerName(topEmployer.name) : '',
          employerPay: topEmployer ? eur(topEmployer.median, loc) : '',
          hasCity: topCity ? 'yes' : 'no',
          city: topCity ? topCity.city.split(',')[0] : '',
          cityPay: topCity ? eur(medianMid(topCity.stats) ?? 0, loc) : '',
          hasLevel: topLevel && levelMsg && (data.roleLevels?.length ?? 0) >= 2 ? 'yes' : 'no',
          level: levelMsg ? formatMessage(levelMsg).toLowerCase() : '',
          levelPay: topLevel ? eur(topLevel.median, loc) : '',
        },
      });
    }
  }
  if (!thinAds && !officialLeads && median != null && data.finlandMedian) {
    const pct = percentVs(median, data.finlandMedian);
    faqEntries.push({
      q: v2Messages.faqQGood,
      a: v2Messages.faqAGood,
      aValues: {
        median: eur(median, loc),
        all: eur(data.finlandMedian, loc),
        pct: Math.abs(pct),
        dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat',
      },
    });
  } else if (officialLeads && officialTotal && data.officialAllMedian) {
    const pct = percentVs(officialTotal.regularMedian, data.officialAllMedian);
    faqEntries.push({
      q: v2Messages.faqQGood,
      a: v2Messages.faqAGoodOfficial,
      aValues: {
        official: eur(officialTotal.regularMedian, loc),
        all: eur(data.officialAllMedian, loc),
        year: officialYear,
        pct: Math.abs(pct),
        dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat',
      },
    });
  }
  // With thin ads the official figure already answered "what does it pay" above.
  if (officialTotal && !officialLeads) {
    faqEntries.push({
      q: v2Messages.faqQActual,
      a: v2Messages.faqAActual,
      aValues: { official: eur(officialTotal.regularMedian, loc), year: officialYear },
    });
  }
  if (!thinAds && !officialLeads && snapshotTrend?.percentChange != null) {
    const pct = snapshotTrend.percentChange;
    faqEntries.push({
      q: v2Messages.faqQTrend,
      a: v2Messages.faqATrend,
      // Locale decimals: "3,3 %" in fi/sv, not "3.3 %".
      aValues: {
        pct: new Intl.NumberFormat(loc === 'en' ? 'en-US' : 'fi-FI', { maximumFractionDigits: 1 }).format(Math.abs(pct)),
        dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat',
      },
    });
  }
  const faq: FaqItem[] = faqEntries.map((e) => ({
    q: <Msg {...e.q} values={qValues} />,
    a: <Msg {...e.a} values={e.aValues} />,
  }));
  // The same questions and answers as plain text for search engines and AI answers.
  const faqJsonLd = faqEntries.length
    ? JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqEntries.map((e) => ({
          '@type': 'Question',
          name: plainText(formatMessage(e.q, qValues)),
          acceptedAnswer: { '@type': 'Answer', text: plainText(formatMessage(e.a, e.aValues)) },
        })),
      }).replace(/</g, '\\u003c')
    : null;

  // Take-home at the low, middle and high of whichever figure leads the page.
  const takeHomeSource: 'official' | 'ads' = officialLeads ? 'official' : 'ads';
  const takeHomeRows: TakeHomeRow[] =
    takeHomeSource === 'official' && officialTotal
      ? [
          ...(officialTotal.regularP10 ? [{ label: <Msg {...v2Messages.takeHomeRowLow} values={{ source: 'official' }} />, gross: officialTotal.regularP10 }] : []),
          { label: <Msg {...v2Messages.takeHomeRowMid} />, gross: officialTotal.regularMedian },
          ...(officialTotal.regularP90 ? [{ label: <Msg {...v2Messages.takeHomeRowHigh} values={{ source: 'official' }} />, gross: officialTotal.regularP90 }] : []),
        ]
      : !thinAds && median != null && overallStats?.percentile25 && overallStats?.percentile75
        ? [
            { label: <Msg {...v2Messages.takeHomeRowLow} values={{ source: 'ads' }} />, gross: overallStats.percentile25.min },
            { label: <Msg {...v2Messages.takeHomeRowMid} />, gross: median },
            { label: <Msg {...v2Messages.takeHomeRowHigh} values={{ source: 'ads' }} />, gross: overallStats.percentile75.max },
          ]
        : [];

  const showTopPaying = (data.market?.topPaying.length ?? 0) > 0;
  const breadcrumbs = (
    <BreadcrumbNavigation
      className="px-0"
      items={[{ label: formatMessage(messages.breadcrumbSalaryData), href: breadcrumbHref }, { label: displayTitle }]}
    />
  );

  // Official-figure roles have no ads row but still get the full header (figure, lede, tabs).
  const header = overallStats || data.official?.total ? (
    <SalaryHeader
      data={data}
      roleTitle={displayTitle}
      headingTitle={headingTitle}
      otherTitles={otherTitles}
      locale={loc}
      jobsHref={jobsHref}
      salaryHref={salaryHref}
      breadcrumbs={breadcrumbs}
    />
  ) : (
    <PageHeader
      breadcrumbs={breadcrumbs}
      title={<Msg {...v2Messages.headingWithYear} values={{ roleTitle: headingTitle, year }} />}
      subtitle={
        <>
          {otherTitles.length > 0 ? (
            <>
              <Msg {...messages.alsoKnownAs} values={{ titles: otherTitles.join(', ') }} />
              <br />
            </>
          ) : null}
          <Msg {...messages.intro} />{' '}
          <Link href={jobsHref} className="font-semibold text-jc-ink underline underline-offset-4">
            <Msg {...messages.seeOpenJobs} values={{ roleTitle: displayTitle }} />
          </Link>
        </>
      }
    />
  );

  return (
    <div className="min-h-screen bg-jc-page">
      <script
        id={SALARY_ROLE_PAGE_SCRIPT_ID}
        type="application/json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, '\\u003c') }}
      />
      {/* The salary template: header, the range beside the readout, the method note. Everything the
          role page adds follows as calm sections in the same column and rhythm. */}
      <SalaryTemplate
        header={header}
        distribution={overallStats ? <SalaryRange data={data} stats={overallStats} locale={loc} /> : undefined}
        readout={
          overallStats ? (
            <SalaryReadout
              data={data}
              stats={overallStats}
              trend={snapshotTrend}
              locale={loc}
              showNet={showNet}
              onShowNet={setShowNet}
            />
          ) : undefined
        }
        methodNote={
          <InlineAlert
            tone="info"
            title={
              <Link
                href="/salary-methodology"
                locale={locale}
                className="text-jc-ink underline underline-offset-4 hover:no-underline"
              >
                <Msg {...messages.methodologyLink} />
              </Link>
            }
          >
            <Msg {...v2Messages.disclaimerV2} />
          </InlineAlert>
        }
      />
      <PageContainer>
        <div className="flex flex-col gap-jc-section pb-jc-section">
          <RoleIntro data={data} />
          <TesMinimumSection data={data} locale={loc} />
          <InsightTiles data={data} locale={loc} medianMid={median} />
          {median != null && overallStats && sampleSize >= 5 ? (
            <PayCheck data={data} locale={loc} medianMid={median} jobsHref={jobsHref} />
          ) : null}
          <SectorFinding data={data} locale={loc} roleTitle={displayTitle} canonicalPath={salaryHref} />
          <EmployerPayChart data={data} locale={loc} medianMid={median} />
          <OfficialEarningsPanel data={data} locale={loc} medianMid={median} />
          <TakeHomeTable rows={takeHomeRows} source={takeHomeSource} locale={loc} />
          <CityTable cityStats={cityStats} roleTitle={displayTitle} locale={loc} />
          <RoleLevelLadder data={data} locale={loc} />
          {remoteStats && (remoteStats.sampleSize ?? remoteStats.count ?? 0) >= MIN_CITY_SAMPLE ? (
            <RemoteStatsPanel stats={remoteStats} roleTitle={displayTitle} locale={loc} />
          ) : null}
          <AdsAskOffer data={data} />
          <TopPayingJobs data={data} locale={loc} medianMid={median} jobsHref={jobsHref} />
          {!showTopPaying ? <ExamplePostings postings={examplePostings} /> : null}
          <CompaniesHiringForRole
            roleTitle={displayTitle}
            locale={locale}
            hiring={hiringCompanies ?? { live: false, companies: [] }}
          />
          <OccupationAbout data={data} roleNom={roleNom} />
          <ExposureBadge exposure={exposure} relatedRoles={exposureRelatedRoles} locale={locale} />
          <SalaryFaq items={faq} title={<Msg {...v2Messages.faqTitle} values={{ roleTitle: roleNom, roleLead }} />} />
          {faqJsonLd ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} /> : null}

          <Card flat className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-jc-ink">
              <Msg {...messages.ctaBanner} values={{ roleTitle: displayTitle, link: '' }} />
            </p>
            <Button href={jobsHref} variant="primary">
              {formatMessage(messages.ctaBannerLink)}
            </Button>
          </Card>

          <HubCrossLinks
            variant="chips"
            locale={hubLocale}
            heading={hubLinksHeading(hubLocale)}
            links={[
              ...((): HubCrossLink[] => {
                const hub =
                  findRoleByDisplayName(roleTitle) ??
                  findRoleByDisplayName(displayTitle) ??
                  findRoleForAnyLocaleTitle(roleTitle);
                if (!hub) return [];
                const name = hub.displayNames[hubLocale];
                const label =
                  hubLocale === 'fi'
                    ? `${name} työpaikat`
                    : hubLocale === 'sv'
                      ? `Lediga jobb som ${name}`
                      : `${name} jobs in Finland`;
                return [{ kind: 'role', href: buildRoleHubPath(resolveRoleHubSlug(hub, hubLocale), hubLocale), label }];
              })(),
              ...buildHubIndexLinks(hubLocale, ['salary', 'roles', 'employers']),
            ]}
          />
        </div>
      </PageContainer>
    </div>
  );
}
